import type {
  ConciergeConfigPatch,
  ConciergeConfigResponse,
  CustomFieldDefinition,
  CustomFieldInput,
  CustomFieldPatch,
  CustomFieldValue,
  CustomFieldValueInput,
  PriceQuote,
  PricingRule,
  PricingRuleInput,
  PricingRulePatch,
  ServicePaymentMode,
  ServiceQuoteRequest,
  ServiceRecurrenceSettings,
  ServiceSummary,
  SlotHoldRequest,
  SlotHoldResult,
  TaxDefinition,
  TaxInput,
  TaxPatch
} from "@kuroo/contracts";
import type {
  CatalogCategory,
  CatalogExtra,
  CatalogLocation,
  CatalogPackage,
  CatalogProvider,
  CatalogResource,
  LocationInput,
  LocationPatch
} from "./client-catalog-types";

export type SdkCatalogContext = {
  request: <T>(path: string, init?: RequestInit) => Promise<T>;
};

export function services(context: SdkCatalogContext, options: { organizationSlug?: string; signal?: AbortSignal } = {}): Promise<ServiceSummary[]> {
  const query = new URLSearchParams();
  if (options.organizationSlug) query.set("organizationSlug", options.organizationSlug);
  return context.request<ServiceSummary[]>(`/api/v1/catalog/services${query.size ? `?${query}` : ""}`, { signal: options.signal });
}

export function createService(context: SdkCatalogContext, input: { name: string; durationMinutes: number; priceCents: number; currency?: string; capacity?: number; priceUnit?: ServiceSummary["priceUnit"]; status?: ServiceSummary["status"]; categoryId?: string; paymentMode?: ServicePaymentMode; googleMeetEnabled?: boolean; bufferBeforeMinutes?: number; bufferAfterMinutes?: number; slotIntervalMinutes?: number | null; minNoticeMinutes?: number; maxAdvanceDays?: number | null }): Promise<ServiceSummary> {
  return context.request<ServiceSummary>("/api/v1/catalog/services", { method: "POST", body: JSON.stringify(input) });
}

export function service(context: SdkCatalogContext, serviceId: string): Promise<ServiceSummary> {
  return context.request<ServiceSummary>(`/api/v1/catalog/services/${encodeURIComponent(serviceId)}`);
}

export function updateService(context: SdkCatalogContext, serviceId: string, input: { name?: string; slug?: string; description?: string | null; durationMinutes?: number; priceCents?: number; currency?: string; capacity?: number; priceUnit?: ServiceSummary["priceUnit"]; status?: ServiceSummary["status"]; categoryId?: string | null; paymentMode?: ServicePaymentMode; googleMeetEnabled?: boolean; bufferBeforeMinutes?: number; bufferAfterMinutes?: number; slotIntervalMinutes?: number | null; minNoticeMinutes?: number; maxAdvanceDays?: number | null; recurrence?: Partial<ServiceRecurrenceSettings> }): Promise<ServiceSummary> {
  return context.request<ServiceSummary>(`/api/v1/catalog/services/${encodeURIComponent(serviceId)}`, { method: "PATCH", body: JSON.stringify(input) });
}

export function deleteService(context: SdkCatalogContext, serviceId: string): Promise<{ id: string; status: "archived"; deleted: boolean }> {
  return context.request<{ id: string; status: "archived"; deleted: boolean }>(`/api/v1/catalog/services/${encodeURIComponent(serviceId)}`, { method: "DELETE" });
}

export function pricingRules(context: SdkCatalogContext, filters: { targetKind?: PricingRule["targetKind"]; targetId?: string; enabled?: boolean } = {}): Promise<PricingRule[]> {
  const query = new URLSearchParams();
  if (filters.targetKind) query.set("targetKind", filters.targetKind);
  if (filters.targetId) query.set("targetId", filters.targetId);
  if (filters.enabled !== undefined) query.set("enabled", String(filters.enabled));
  return context.request<PricingRule[]>(`/api/v1/pricing/rules${query.size ? `?${query}` : ""}`);
}

export function createPricingRule(context: SdkCatalogContext, input: PricingRuleInput): Promise<PricingRule> {
  return context.request<PricingRule>("/api/v1/pricing/rules", { method: "POST", body: JSON.stringify(input) });
}

export function updatePricingRule(context: SdkCatalogContext, ruleId: string, input: PricingRulePatch): Promise<PricingRule> {
  return context.request<PricingRule>(`/api/v1/pricing/rules/${encodeURIComponent(ruleId)}`, { method: "PATCH", body: JSON.stringify(input) });
}

export function deletePricingRule(context: SdkCatalogContext, ruleId: string): Promise<{ id: string; deleted: boolean }> {
  return context.request<{ id: string; deleted: boolean }>(`/api/v1/pricing/rules/${encodeURIComponent(ruleId)}`, { method: "DELETE" });
}

export function quoteService(context: SdkCatalogContext, input: ServiceQuoteRequest, options: { signal?: AbortSignal } = {}): Promise<PriceQuote> {
  return context.request<PriceQuote>("/api/v1/pricing/quote", { method: "POST", body: JSON.stringify(input), signal: options.signal, headers: input.idempotencyKey ? { "Idempotency-Key": input.idempotencyKey } : undefined });
}

/** Calculates and serializes a final service proposal in one operation. */
export function quoteAndHoldService(context: SdkCatalogContext, input: SlotHoldRequest, options: { signal?: AbortSignal } = {}): Promise<SlotHoldResult> {
  return context.request<SlotHoldResult>("/api/v1/pricing/quote-and-hold", { method: "POST", body: JSON.stringify(input), signal: options.signal, headers: { "Idempotency-Key": input.idempotencyKey } });
}

/** Generic quote facade retained for integrations that pass a forward-compatible payload. */
export function quote(context: SdkCatalogContext, input: Record<string, unknown> & { idempotencyKey?: string }): Promise<PriceQuote> {
  return context.request<PriceQuote>("/api/v1/pricing/quote", { method: "POST", body: JSON.stringify(input), headers: input.idempotencyKey ? { "Idempotency-Key": input.idempotencyKey } : undefined });
}

export function categories(context: SdkCatalogContext, options: { includeArchived?: boolean; status?: CatalogCategory["status"] } = {}): Promise<CatalogCategory[]> {
  const query = new URLSearchParams();
  if (options.includeArchived) query.set("includeArchived", "true");
  if (options.status) query.set("status", options.status);
  return context.request<CatalogCategory[]>(`/api/v1/catalog/categories${query.size ? `?${query}` : ""}`);
}

export function createCategory(context: SdkCatalogContext, input: { name: string; slug?: string; description?: string; sortOrder?: number; status?: CatalogCategory["status"] }): Promise<Pick<CatalogCategory, "id" | "name" | "slug">> {
  return context.request<Pick<CatalogCategory, "id" | "name" | "slug">>("/api/v1/catalog/categories", { method: "POST", body: JSON.stringify(input) });
}

export function category(context: SdkCatalogContext, categoryId: string): Promise<CatalogCategory> {
  return context.request<CatalogCategory>(`/api/v1/catalog/categories/${encodeURIComponent(categoryId)}`);
}

export function updateCategory(context: SdkCatalogContext, categoryId: string, input: { name?: string; slug?: string; description?: string | null; status?: CatalogCategory["status"]; sortOrder?: number }): Promise<CatalogCategory> {
  return context.request<CatalogCategory>(`/api/v1/catalog/categories/${encodeURIComponent(categoryId)}`, { method: "PATCH", body: JSON.stringify(input) });
}

export function deleteCategory(context: SdkCatalogContext, categoryId: string): Promise<{ id: string; status: "archived"; deleted: boolean }> {
  return context.request<{ id: string; status: "archived"; deleted: boolean }>(`/api/v1/catalog/categories/${encodeURIComponent(categoryId)}`, { method: "DELETE" });
}

export function taxes(context: SdkCatalogContext, options: { status?: TaxDefinition["status"]; signal?: AbortSignal } = {}): Promise<TaxDefinition[]> {
  const query = new URLSearchParams();
  if (options.status) query.set("status", options.status);
  return context.request<TaxDefinition[]>(`/api/v1/taxes${query.size ? `?${query}` : ""}`, { signal: options.signal });
}

export function createTax(context: SdkCatalogContext, input: TaxInput): Promise<TaxDefinition> {
  return context.request<TaxDefinition>("/api/v1/taxes", { method: "POST", body: JSON.stringify(input) });
}

export function tax(context: SdkCatalogContext, taxId: string): Promise<TaxDefinition> {
  return context.request<TaxDefinition>(`/api/v1/taxes/${encodeURIComponent(taxId)}`);
}

export function updateTax(context: SdkCatalogContext, taxId: string, input: TaxPatch): Promise<TaxDefinition> {
  return context.request<TaxDefinition>(`/api/v1/taxes/${encodeURIComponent(taxId)}`, { method: "PATCH", body: JSON.stringify(input) });
}

export function deleteTax(context: SdkCatalogContext, taxId: string): Promise<{ id: string; status: "inactive"; deleted: boolean }> {
  return context.request<{ id: string; status: "inactive"; deleted: boolean }>(`/api/v1/taxes/${encodeURIComponent(taxId)}`, { method: "DELETE" });
}

export function updateTaxStatus(context: SdkCatalogContext, taxId: string, status: TaxDefinition["status"]): Promise<{ id: string; status: TaxDefinition["status"] }> {
  return context.request<{ id: string; status: TaxDefinition["status"] }>(`/api/v1/taxes/${encodeURIComponent(taxId)}/status`, { method: "POST", body: JSON.stringify({ status }) });
}

export function customFields(context: SdkCatalogContext, options: { entityType?: CustomFieldDefinition["entityType"]; status?: CustomFieldDefinition["status"]; includeInactive?: boolean; signal?: AbortSignal } = {}): Promise<CustomFieldDefinition[]> {
  const query = new URLSearchParams();
  if (options.entityType) query.set("entityType", options.entityType);
  if (options.status) query.set("status", options.status);
  if (options.includeInactive) query.set("includeInactive", "true");
  return context.request<CustomFieldDefinition[]>(`/api/v1/custom-fields${query.size ? `?${query}` : ""}`, { signal: options.signal });
}

export function createCustomField(context: SdkCatalogContext, input: CustomFieldInput): Promise<CustomFieldDefinition> {
  return context.request<CustomFieldDefinition>("/api/v1/custom-fields", { method: "POST", body: JSON.stringify(input) });
}

export function customField(context: SdkCatalogContext, fieldId: string): Promise<CustomFieldDefinition> {
  return context.request<CustomFieldDefinition>(`/api/v1/custom-fields/${encodeURIComponent(fieldId)}`);
}

export function updateCustomField(context: SdkCatalogContext, fieldId: string, input: CustomFieldPatch): Promise<CustomFieldDefinition> {
  return context.request<CustomFieldDefinition>(`/api/v1/custom-fields/${encodeURIComponent(fieldId)}`, { method: "PATCH", body: JSON.stringify(input) });
}

export function deleteCustomField(context: SdkCatalogContext, fieldId: string): Promise<{ id: string; status: "inactive"; deleted: boolean }> {
  return context.request<{ id: string; status: "inactive"; deleted: boolean }>(`/api/v1/custom-fields/${encodeURIComponent(fieldId)}`, { method: "DELETE" });
}

export function customFieldValue(context: SdkCatalogContext, fieldId: string, entityId: string): Promise<CustomFieldValue> {
  return context.request<CustomFieldValue>(`/api/v1/custom-fields/${encodeURIComponent(fieldId)}/values/${encodeURIComponent(entityId)}`);
}

export function setCustomFieldValue(context: SdkCatalogContext, fieldId: string, entityId: string, input: CustomFieldValueInput): Promise<CustomFieldValue> {
  return context.request<CustomFieldValue>(`/api/v1/custom-fields/${encodeURIComponent(fieldId)}/values/${encodeURIComponent(entityId)}`, { method: "PUT", body: JSON.stringify(input) });
}

export function updateCustomFieldValue(context: SdkCatalogContext, fieldId: string, entityId: string, input: CustomFieldValueInput): Promise<CustomFieldValue> {
  return context.request<CustomFieldValue>(`/api/v1/custom-fields/${encodeURIComponent(fieldId)}/values/${encodeURIComponent(entityId)}`, { method: "PATCH", body: JSON.stringify(input) });
}

export function deleteCustomFieldValue(context: SdkCatalogContext, fieldId: string, entityId: string): Promise<{ fieldId: string; entityId: string; deleted: boolean }> {
  return context.request<{ fieldId: string; entityId: string; deleted: boolean }>(`/api/v1/custom-fields/${encodeURIComponent(fieldId)}/values/${encodeURIComponent(entityId)}`, { method: "DELETE" });
}

export function settings(context: SdkCatalogContext): Promise<Array<{ key: string; value: unknown; encrypted: boolean; updatedAt: string }>> {
  return context.request<Array<{ key: string; value: unknown; encrypted: boolean; updatedAt: string }>>("/api/v1/settings");
}

export function updateSettings(context: SdkCatalogContext, settings: Record<string, unknown>): Promise<Record<string, unknown>> {
  return context.request<Record<string, unknown>>("/api/v1/settings", { method: "PATCH", body: JSON.stringify({ settings }) });
}

export function conciergeConfig(context: SdkCatalogContext): Promise<ConciergeConfigResponse> {
  return context.request<ConciergeConfigResponse>("/api/v1/concierge/config");
}

export function updateConciergeConfig(context: SdkCatalogContext, config: ConciergeConfigPatch): Promise<ConciergeConfigResponse> {
  return context.request<ConciergeConfigResponse>("/api/v1/concierge/config", { method: "PATCH", body: JSON.stringify(config) });
}

export function extras(context: SdkCatalogContext, serviceId?: string, options: { signal?: AbortSignal; includeInactive?: boolean; status?: CatalogExtra["status"] } = {}): Promise<CatalogExtra[]> {
  const query = new URLSearchParams();
  if (serviceId) query.set("serviceId", serviceId);
  if (options.includeInactive) query.set("includeInactive", "true");
  if (options.status) query.set("status", options.status);
  return context.request<CatalogExtra[]>(`/api/v1/catalog/extras${query.size ? `?${query}` : ""}`, { signal: options.signal });
}

export function createExtra(context: SdkCatalogContext, input: { serviceId: string; name: string; description?: string; priceCents?: number; durationMinutes?: number; maxQuantity?: number; status?: "active" | "inactive" }): Promise<{ id: string; serviceId: string; name: string }> {
  return context.request<{ id: string; serviceId: string; name: string }>("/api/v1/catalog/extras", { method: "POST", body: JSON.stringify(input) });
}

export function extra(context: SdkCatalogContext, extraId: string): Promise<CatalogExtra> {
  return context.request<CatalogExtra>(`/api/v1/catalog/extras/${encodeURIComponent(extraId)}`);
}

export function updateExtra(context: SdkCatalogContext, extraId: string, input: { serviceId?: string; name?: string; description?: string | null; priceCents?: number; durationMinutes?: number; maxQuantity?: number; status?: CatalogExtra["status"] }): Promise<CatalogExtra> {
  return context.request<CatalogExtra>(`/api/v1/catalog/extras/${encodeURIComponent(extraId)}`, { method: "PATCH", body: JSON.stringify(input) });
}

export function deleteExtra(context: SdkCatalogContext, extraId: string): Promise<{ id: string; status: "inactive"; deleted: boolean }> {
  return context.request<{ id: string; status: "inactive"; deleted: boolean }>(`/api/v1/catalog/extras/${encodeURIComponent(extraId)}`, { method: "DELETE" });
}

export function packages(context: SdkCatalogContext, options: { includeArchived?: boolean; status?: CatalogPackage["status"] } = {}): Promise<CatalogPackage[]> {
  const query = new URLSearchParams();
  if (options.includeArchived) query.set("includeArchived", "true");
  if (options.status) query.set("status", options.status);
  return context.request<CatalogPackage[]>(`/api/v1/catalog/packages${query.size ? `?${query}` : ""}`);
}

export function createPackage(context: SdkCatalogContext, input: { name: string; slug?: string; description?: string; priceCents?: number; currency?: string; capacity?: number; status?: CatalogPackage["status"]; validFrom?: string; validUntil?: string }): Promise<Pick<CatalogPackage, "id" | "name" | "slug">> {
  return context.request<Pick<CatalogPackage, "id" | "name" | "slug">>("/api/v1/catalog/packages", { method: "POST", body: JSON.stringify(input) });
}

export function getPackage(context: SdkCatalogContext, packageId: string): Promise<CatalogPackage> {
  return context.request<CatalogPackage>(`/api/v1/catalog/packages/${encodeURIComponent(packageId)}`);
}

export function updatePackage(context: SdkCatalogContext, packageId: string, input: { name?: string; slug?: string; description?: string | null; priceCents?: number; currency?: string; capacity?: number; status?: CatalogPackage["status"]; validFrom?: string | null; validUntil?: string | null }): Promise<CatalogPackage> {
  return context.request<CatalogPackage>(`/api/v1/catalog/packages/${encodeURIComponent(packageId)}`, { method: "PATCH", body: JSON.stringify(input) });
}

export function deletePackage(context: SdkCatalogContext, packageId: string): Promise<{ id: string; status: "archived"; deleted: boolean }> {
  return context.request<{ id: string; status: "archived"; deleted: boolean }>(`/api/v1/catalog/packages/${encodeURIComponent(packageId)}`, { method: "DELETE" });
}

export function locations(context: SdkCatalogContext, options: { serviceId?: string; providerId?: string; organizationSlug?: string; signal?: AbortSignal } = {}): Promise<CatalogLocation[]> {
  const query = new URLSearchParams();
  if (options.serviceId) query.set("serviceId", options.serviceId);
  if (options.providerId) query.set("providerId", options.providerId);
  if (options.organizationSlug) query.set("organizationSlug", options.organizationSlug);
  return context.request<CatalogLocation[]>(`/api/v1/catalog/locations${query.size ? `?${query.toString()}` : ""}`, { signal: options.signal });
}

export function createLocation(context: SdkCatalogContext, input: LocationInput): Promise<CatalogLocation> {
  return context.request<CatalogLocation>("/api/v1/catalog/locations", { method: "POST", body: JSON.stringify(input) });
}

export function updateLocation(context: SdkCatalogContext, locationId: string, input: LocationPatch): Promise<CatalogLocation> {
  return context.request<CatalogLocation>(`/api/v1/catalog/locations/${encodeURIComponent(locationId)}`, { method: "PATCH", body: JSON.stringify(input) });
}

export function location(context: SdkCatalogContext, locationId: string): Promise<CatalogLocation> {
  return context.request<CatalogLocation>(`/api/v1/catalog/locations/${encodeURIComponent(locationId)}`);
}

export function deleteLocation(context: SdkCatalogContext, locationId: string): Promise<{ id: string; status: "inactive"; deleted: boolean }> {
  return context.request<{ id: string; status: "inactive"; deleted: boolean }>(`/api/v1/catalog/locations/${encodeURIComponent(locationId)}`, { method: "DELETE" });
}

export function providers(context: SdkCatalogContext, options: { serviceId?: string; organizationSlug?: string; signal?: AbortSignal } = {}): Promise<CatalogProvider[]> {
  const query = new URLSearchParams();
  if (options.serviceId) query.set("serviceId", options.serviceId);
  if (options.organizationSlug) query.set("organizationSlug", options.organizationSlug);
  return context.request<CatalogProvider[]>(`/api/v1/catalog/providers${query.size ? `?${query.toString()}` : ""}`, { signal: options.signal });
}

export function resources(context: SdkCatalogContext, locationId?: string, options: { includeInactive?: boolean; status?: CatalogResource["status"] } = {}): Promise<CatalogResource[]> {
  const query = new URLSearchParams();
  if (locationId) query.set("locationId", locationId);
  if (options.includeInactive) query.set("includeInactive", "true");
  if (options.status) query.set("status", options.status);
  return context.request<CatalogResource[]>(`/api/v1/catalog/resources${query.size ? `?${query}` : ""}`);
}

export function resource(context: SdkCatalogContext, resourceId: string): Promise<CatalogResource> {
  return context.request<CatalogResource>(`/api/v1/catalog/resources/${encodeURIComponent(resourceId)}`);
}

export function updateResource(context: SdkCatalogContext, resourceId: string, input: { locationId?: string | null; name?: string; type?: string; capacity?: number; status?: CatalogResource["status"]; metadata?: Record<string, unknown> }): Promise<CatalogResource> {
  return context.request<CatalogResource>(`/api/v1/catalog/resources/${encodeURIComponent(resourceId)}`, { method: "PATCH", body: JSON.stringify(input) });
}

export function createResource(context: SdkCatalogContext, input: { locationId?: string | null; name: string; type?: string; capacity?: number; status?: CatalogResource["status"]; metadata?: Record<string, unknown> }): Promise<CatalogResource> {
  return context.request<CatalogResource>("/api/v1/catalog/resources", { method: "POST", body: JSON.stringify(input) });
}

export function deleteResource(context: SdkCatalogContext, resourceId: string): Promise<{ id: string; status: "inactive"; deleted: boolean }> {
  return context.request<{ id: string; status: "inactive"; deleted: boolean }>(`/api/v1/catalog/resources/${encodeURIComponent(resourceId)}`, { method: "DELETE" });
}
